export type InteractiveShellBootSurface = 'editor' | 'projects' | 'home' | 'saas';

export const INTERACTIVE_SHELL_LOCATION_CHANGE_EVENT = 'interactiveshell:locationchange';

/**
 * Browser history mutation does not emit popstate. Interactive Shell has two top-level
 * React applications (SaaS + editor), so history changes must be observable at
 * that boundary as well as by the inner routers. Install this once before
 * services are created so /projects -> /ws deep links cannot remain trapped in
 * the SaaS shell.
 */
export function installInteractiveShellLocationChangeBridge(windowRef: Window): void {
  const history = windowRef.history as History & { __interactiveShellLocationBridge?: boolean };
  if (history.__interactiveShellLocationBridge) return;
  history.__interactiveShellLocationBridge = true;
  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);
  history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
    originalPushState(data, unused, url);
    windowRef.dispatchEvent(new Event(INTERACTIVE_SHELL_LOCATION_CHANGE_EVENT));
  };
  history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
    originalReplaceState(data, unused, url);
    windowRef.dispatchEvent(new Event(INTERACTIVE_SHELL_LOCATION_CHANGE_EVENT));
  };
}

/**
 * Chooses the top-level Interactive Shell application from the browser entry URL.
 * `/ws` is reserved for the local editor shell. Legacy `route=welcome`
 * entries now return to the public home page, while a truly bare `/ws`
 * remains the compatibility redirect to Projects. Other hashes are editor
 * deep links.
 */
export function resolveInteractiveShellBootSurface(
  pathname: string,
  hash: string,
): InteractiveShellBootSurface {
  const isWsPath = pathname === '/ws' || pathname.startsWith('/ws/');
  if (!isWsPath) return 'saas';

  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  if (hashParams.get('route') === 'welcome') return 'home';

  return hash ? 'editor' : 'projects';
}
